/**
 * JoinEntityModal
 * Modal voor het joinen van bestaande entities
 */

import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase';
import { collection, query, where, getDocs, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { networkClubService } from '../../services/networkClubService';
import { NetworkClub } from '../../types/networkclub';
import { Icon } from '../icons';
import { Search, Building2, Users, X, Loader, CheckCircle } from '../icons';
import { invalidateAIVisibilityCache } from '../../services/aiVisibilityImpactService';

interface JoinEntityModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const JoinEntityModal: React.FC<JoinEntityModalProps> = ({ onClose, onSuccess }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [clubs, setClubs] = useState<NetworkClub[]>([]);
  const [loading, setLoading] = useState(false);
  const [joiningId, setJoiningId] = useState<string | null>(null);

  useEffect(() => {
    loadClubs();
  }, []);

  const loadClubs = async () => {
    setLoading(true);
    try {
      // Haal alleen actieve, publieke clubs op
      const clubsData = await networkClubService.getNetworkClubs();
      const publicClubs = clubsData.filter(c => c.visibility === 'public' && c.isActive);
      setClubs(publicClubs);
    } catch (error) {
      console.error('Error loading clubs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (clubId: string, entityId: string) => {
    setJoiningId(clubId);
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Check of gebruiker al lid is
      const memberRef = doc(db, 'entityMembers', `${entityId}_${user.uid}`);
      const memberSnap = await getDocs(query(collection(db, 'entityMembers'), where('entityId', '==', entityId), where('userId', '==', user.uid)));
      
      if (!memberSnap.empty) {
        alert('Je bent al lid van deze club');
        setJoiningId(null);
        return;
      }

      // Maak entityMember aan
      await setDoc(memberRef, {
        entityId: entityId,
        userId: user.uid,
        role: 'member',
        createdAt: serverTimestamp(),
        createdBy: user.uid,
      });

      // Update member count en visibility
      await networkClubService.updateClubVisibility(clubId);

      // Invalidate AI Visibility cache (club join affects score)
      await invalidateAIVisibilityCache(user.uid);

      onSuccess();
    } catch (error) {
      console.error('Error joining club:', error);
      alert('Fout bij joinen van club');
    } finally {
      setJoiningId(null);
    }
  };

  const filteredClubs = clubs.filter(club =>
    club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    club.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-5 text-white rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Icon icon={Users} size={20} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-xl font-bold">Lid Worden</h3>
                <p className="text-white/80 text-sm">Word lid van een netwerkclub, mastermind of community</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <Icon icon={X} size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon icon={Search} size={16} strokeWidth={1.5} color="#6b7280" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Zoek clubs, masterminds, communities..."
            />
          </div>
        </div>

        {/* Clubs List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Icon icon={Loader} size={24} strokeWidth={1.5} className="animate-spin text-orange-500" />
            </div>
          ) : filteredClubs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Geen clubs gevonden</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredClubs.map(club => (
                <div
                  key={club.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Icon icon={Building2} size={24} strokeWidth={1.5} className="text-orange-500" />
                        <div>
                          <h4 className="font-semibold text-gray-900">{club.name}</h4>
                          <p className="text-sm text-gray-500">{club.type === 'networkclub' ? 'Netwerkclub' : club.type === 'mastermind' ? 'Mastermind' : 'Community'}</p>
                        </div>
                      </div>
                      {club.description && (
                        <p className="text-sm text-gray-600 mb-2">{club.description}</p>
                      )}
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        {club.region && (
                          <span>{club.region}</span>
                        )}
                        {club.memberCount !== undefined && (
                          <span>{club.memberCount} leden</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => club.entityId && handleJoin(club.id, club.entityId)}
                      disabled={joiningId === club.id || !club.entityId}
                      className="ml-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {joiningId === club.id ? (
                        <>
                          <Icon icon={Loader} size={16} strokeWidth={1.5} className="animate-spin" />
                          <span>Joinen...</span>
                        </>
                      ) : (
                        <>
                          <Icon icon={CheckCircle} size={16} strokeWidth={1.5} />
                          <span>Lid Worden</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JoinEntityModal;
